import { useState, useEffect } from "react";
import LinearProgress from "@mui/material/LinearProgress";
import {
  Button,
  Box,
  Typography,
  Stack,
  Chip,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Divider,
  Alert,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Close, DirectionsCar, PersonAdd } from "@mui/icons-material";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import Tooltip from "@mui/material/Tooltip";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import Pagination from "@mui/material/Pagination";

const theme = createTheme({
  direction: "rtl",
  typography: {
    fontFamily: '"Cairo", "Tajawal", Arial, sans-serif',
  },
});

const API_BASE = "https://dash.tatabu.io/back-end/public/api/taxi-orders";

const formatPhoneForWhatsApp = (phone) => {
  if (!phone) return null;
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10 && cleaned.startsWith("05")) return "966" + cleaned.slice(1);
  if (cleaned.length === 9 && cleaned.startsWith("5")) return "966" + cleaned;
  if (cleaned.length === 12 && cleaned.startsWith("966")) return cleaned;
  return cleaned.startsWith("0") ? "966" + cleaned.slice(1) : cleaned;
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit" });
};

const formatTime12h = (timeStr) => {
  if (!timeStr) return null;
  const [hourStr, minuteStr] = timeStr.split(":");
  let hour = parseInt(hourStr, 10);
  const minute = minuteStr || "00";
  const period = hour >= 12 ? "م" : "ص";
  hour = hour % 12 || 12;
  return `${hour}:${minute} ${period}`;
};

const statusMap = {
  ناقص: { label: "ناقص", color: "warning" },
  "مؤكد غير مدفوع": { label: "مؤكد غير مدفوع", color: "info" },
  "مؤكد مدفوع": { label: "مؤكد مدفوع", color: "success" },
  "تذكير أول": { label: "تذكير أول", color: "success" },
  "تذكير ثاني": { label: "تذكير ثاني", color: "success" },
  استبيان: { label: "استبيان", color: "success" },
  مكتمل: { label: "مكتمل", color: "success" },
  ملغي: { label: "ملغي", color: "error" },
};

// Which driver fields are missing — returns array of missing field labels
const getMissingFields = (row) => {
  const missing = [];
  if (!row.driver_name) missing.push("اسم السائق");
  if (!row.driver_phone_number) missing.push("رقم السائق");
  if (row.driver_amount == null) missing.push("مبلغ السائق");
  return missing;
};

export default function OrdersWithoutDriverDetails() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const rowsPerPage = 12;
  const paginatedOrders = orders.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const handlePageChange = (_, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [driverName, setDriverName] = useState("");
  const [driverPhone, setDriverPhone] = useState("");
  const [driverAmount, setDriverAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/without-driver-details`);
        const json = await res.json();
        if (json.success) {
          setOrders(json.data.orders || []);
        } else {
          setError("فشل في تحميل البيانات");
        }
      } catch {
        setError("فشل الاتصال بالخادم");
      }
      setLoading(false);
    };
    fetchOrders();
  }, []);

  const handleOpenTicket = (row) => {
    if (!row.images || row.images.length === 0) {
      alert("لا توجد تذكرة طيران");
      return;
    }
    window.open(row.images[0].full_path, "_blank");
  };

  const handleOpenModal = (row) => {
    setSelectedRow(row);
    setDriverName(row.driver_name || "");
    setDriverPhone(row.driver_phone_number || "");
    setDriverAmount(row.driver_amount != null ? String(row.driver_amount) : "");
    setSaveError(null);
    setSaveSuccess(false);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRow(null);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!selectedRow) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const res = await fetch(`${API_BASE}/update-driver`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: selectedRow.id,
          driver_name: driverName,
          driver_phone_number: driverPhone,
          driver_amount: driverAmount !== "" ? Number(driverAmount) : null,
        }),
      });
      const json = await res.json();
      if (json.success || res.ok) {
        setSaveSuccess(true);
        const allFilled = !!driverName && !!driverPhone && driverAmount !== "";
        setOrders((prev) =>
          prev
            .map((o) => {
              if (o.id !== selectedRow.id) return o;
              const updated = {
                ...o,
                driver_name: driverName,
                driver_phone_number: driverPhone,
                driver_amount: driverAmount !== "" ? Number(driverAmount) : null,
                // status: allFilled ? "confirmed" : "pending",
              };
              return updated;
              // Remove from list if all fields are now filled
            })
            .filter((o) => getMissingFields(o).length > 0)
        );
        setTimeout(() => handleCloseModal(), 1200);
      } else {
        setSaveError(json.message || "حدث خطأ أثناء الحفظ");
      }
    } catch {
      setSaveError("فشل الاتصال بالخادم");
    }
    setSaving(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap" rel="stylesheet" />

      <Box dir="rtl">
        {loading && (
          <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999 }}>
            <LinearProgress />
          </Box>
        )}

        {/* Page Header */}
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            pt: 3,
            pb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            borderBottom: "1.5px solid #f0f0f0",
            backgroundColor: "#fff",
            mb: 3,
          }}
        >
          <Box sx={{ backgroundColor: "#fce4ec", borderRadius: "12px", p: 1, display: "flex", alignItems: "center" }}>
            <WarningAmberRoundedIcon sx={{ color: "#c62828", fontSize: 28 }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "Cairo, sans-serif", fontWeight: 800, fontSize: "20px", color: "#1a1a1a" }}>طلبات بدون بيانات سائق</Typography>
            <Typography sx={{ fontFamily: "Cairo, sans-serif", fontSize: "13px", color: "#999", fontWeight: 500 }}>
              {orders.length} طلب بحاجة إلى إكمال بيانات السائق (تم استبعاد الطلبات ذات الحالة "ملغي" أو "تم التوصيل")
            </Typography>
          </Box>
        </Box>

        {error && (
          <Box sx={{ px: 2, mb: 2 }}>
            <Alert severity="error" sx={{ fontFamily: "Cairo, sans-serif" }}>
              {error}
            </Alert>
          </Box>
        )}

        <Box sx={{ px: { xs: 1, sm: 2 }, pb: 4 }}>
          {/* Empty State */}
          {!loading && orders.length === 0 && (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ mt: 10, gap: 2 }}>
              <Box sx={{ fontSize: "64px" }}>✅</Box>
              <Typography sx={{ fontFamily: "Cairo, sans-serif", fontWeight: 700, fontSize: "18px", color: "#555" }}>جميع الطلبات لديها بيانات سائق مكتملة</Typography>
            </Box>
          )}

          <Grid container spacing={2}>
            {paginatedOrders.map((row) => {
              const statusInfo = statusMap[row.new_status] || { label: row.new_status, color: "default" };
              const missing = getMissingFields(row);

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={row.id}>
                  <Card
                    elevation={2}
                    sx={{
                      borderRadius: 3,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      fontFamily: '"Cairo", sans-serif',
                      //   border: "1px solid #bf360c",
                      transition: "box-shadow 0.2s",
                      "&:hover": { boxShadow: 6 },
                    }}
                  >
                    <CardContent sx={{ flex: 1, pb: 1 }}>
                      {/* Header: order number + ticket icon + status chip */}
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                        <Typography
                          sx={{
                            fontFamily: '"Cairo", sans-serif',
                            fontWeight: 800,
                            fontSize: "0.8rem",
                            color: "#888",
                            bgcolor: "#f5f5f5",
                            px: 1,
                            py: 0.3,
                            borderRadius: 1,
                          }}
                        >
                          {row.order_number}
                        </Typography>

                        <Stack direction="row" alignItems="center" gap={1}>
                          {row.images && row.images.length > 0 && (
                            <Tooltip title="تذكرة الطيران">
                              <PhotoLibraryIcon onClick={() => handleOpenTicket(row)} sx={{ fontSize: 20, cursor: "pointer", color: "#222", "&:hover": { color: "#0d47a1" } }} />
                            </Tooltip>
                          )}
                          <Chip label={statusInfo.label} color={statusInfo.color} size="small" sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 600, fontSize: "0.75rem" }} />
                        </Stack>
                      </Stack>

                      {/* Client name */}
                      <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 800, fontSize: "1rem", color: "#1a1a1a", mb: 0.5 }}>{row.name || "—"}</Typography>

                      {/* Phone */}
                      {row.phone && (
                        <Box mb={1.2} dir="ltr" sx={{ textAlign: "right" }}>
                          <a
                            href={`https://wa.me/${formatPhoneForWhatsApp(row.phone)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#25D366",
                              textDecoration: "none",
                              fontWeight: 700,
                              fontSize: "13px",
                              fontFamily: "Cairo, sans-serif",
                              backgroundColor: "rgba(37,211,102,0.1)",
                              padding: "3px 10px",
                              borderRadius: "8px",
                              display: "inline-block",
                            }}
                          >
                            📱 {row.phone}
                          </a>
                        </Box>
                      )}

                      {/* Info rows */}
                      <Stack direction="row" alignItems="center" gap={0.8} mb={0.8}>
                        <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.75rem", color: "#999", minWidth: 60 }}>الخدمة:</Typography>
                        <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 700, fontSize: "0.88rem", color: "#222" }}>{row.type || "—"}</Typography>
                      </Stack>

                      <Stack direction="row" alignItems="center" gap={0.8} mb={0.8}>
                        <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.75rem", color: "#999", minWidth: 60 }}>السيارة:</Typography>
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <DirectionsCar sx={{ fontSize: 16, color: "#1565c0" }} />
                          <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 600, fontSize: "0.88rem", color: "#333" }}>{row.car_model || "—"}</Typography>
                        </Stack>
                      </Stack>

                      {row.pickup_date && (
                        <Stack direction="row" alignItems="center" gap={0.8} mb={0.8}>
                          <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.75rem", color: "#999", minWidth: 60 }}>الاستقبال:</Typography>
                          <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 600, fontSize: "0.85rem", color: "#333" }}>
                            {formatDate(row.pickup_date)}
                            {row.pickup_hour ? ` — ${formatTime12h(row.pickup_hour)}` : ""}
                          </Typography>
                        </Stack>
                      )}

                      {/* <Stack direction="row" alignItems="center" gap={0.8} mb={1}>
                        <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.75rem", color: "#999", minWidth: 60 }}>الإجمالي:</Typography>
                        <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 700, fontSize: "0.88rem", color: "#333" }}>{row.price} ﷼</Typography>
                      </Stack> */}

                      {/* Missing fields badges */}
                      <Box sx={{ borderRadius: 2, px: 1.5, py: 1, mt: 1, paddingRight: "0" }}>
                        <Stack direction="row" alignItems="center" gap={0.6} mb={0.6}>
                          <PersonAdd sx={{ fontSize: 14, color: "#e65100" }} />
                          <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.72rem", color: "#e65100", fontWeight: 700 }}>البيانات المطلوبة:</Typography>
                        </Stack>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {missing.map((field) => (
                            <Chip
                              key={field}
                              label={field}
                              size="small"
                              sx={{
                                fontFamily: '"Cairo", sans-serif',
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                bgcolor: "#e65100",
                                color: "#fff",
                                borderRadius: "6px",
                                height: "22px",
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    </CardContent>

                    <Divider />

                    <CardActions sx={{ px: 2, py: 1.5 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        onClick={() => handleOpenModal(row)}
                        sx={{
                          fontFamily: '"Cairo", sans-serif',
                          fontWeight: 700,
                          borderRadius: 2,
                          fontSize: "0.82rem",
                          py: 0.8,
                          //   bgcolor: "#e65100",
                          //   "&:hover": { bgcolor: "#bf360c" },
                        }}
                      >
                        إكمال بيانات السائق
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Pagination */}
        {orders.length > rowsPerPage && (
          <Box display="flex" justifyContent="center" alignItems="center" mt={2} mb={4}>
            <Pagination count={Math.ceil(orders.length / rowsPerPage)} page={page} onChange={handlePageChange} color="primary" shape="rounded" hidePrevButton hideNextButton />
          </Box>
        )}
      </Box>

      {/* Driver Details Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="xs" fullWidth dir="rtl" PaperProps={{ sx: { borderRadius: 3, fontFamily: '"Cairo", sans-serif' } }}>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#e65100",
            color: "#fff",
            fontFamily: '"Cairo", sans-serif',
            fontWeight: 700,
            fontSize: "1rem",
            py: 1.5,
            px: 2.5,
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <PersonAdd sx={{ fontSize: 20 }} />
            <span>إكمال بيانات السائق</span>
          </Stack>
          <IconButton onClick={handleCloseModal} size="small" sx={{ color: "#fff" }}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
          <Stack sx={{ marginBottom: "20px" }} gap={2.5}>
            <TextField
              label="اسم السائق"
              value={driverName}
              onChange={(e) => setDriverName(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ dir: "rtl", style: { fontFamily: '"Cairo", sans-serif' } }}
              InputLabelProps={{ style: { fontFamily: '"Cairo", sans-serif' } }}
            />
            <TextField
              label="رقم السائق"
              value={driverPhone}
              onChange={(e) => setDriverPhone(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ dir: "ltr", style: { fontFamily: '"Cairo", sans-serif' } }}
              InputLabelProps={{ style: { fontFamily: '"Cairo", sans-serif' } }}
              placeholder="05xxxxxxxx"
              dir="ltr"
              type="tel"
            />
            <TextField
              label="مبلغ السائق (﷼)"
              value={driverAmount}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || /^\d*\.?\d*$/.test(val)) setDriverAmount(val);
              }}
              fullWidth
              size="small"
              inputProps={{ dir: "ltr", style: { fontFamily: '"Cairo", sans-serif' } }}
              InputLabelProps={{ style: { fontFamily: '"Cairo", sans-serif' } }}
              type="number"
            />
          </Stack>

          {selectedRow && (
            <>
              <Typography
                variant="body2"
                sx={{
                  mb: 1.5,
                  color: "#555",
                  fontFamily: '"Cairo", sans-serif',
                  bgcolor: "#f5f5f5",
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 0.8,
                  fontSize: "0.82rem",
                }}
              >
                رقم الطلب: <strong>{selectedRow.order_number}</strong> — {selectedRow.type}
                {selectedRow.car_model ? ` — ${selectedRow.car_model}` : ""}
              </Typography>

              <Box sx={{ mb: 1, bgcolor: "#f9f9f9", border: "1px solid #e8e8e8", borderRadius: 2, overflow: "hidden" }}>
                {[
                  { label: "اسم العميل", value: selectedRow.name },
                  { label: "رقم الجوال", value: selectedRow.phone, ltr: true, whatsapp: true },
                  {
                    label: "تاريخ الاستقبال",
                    value:
                      selectedRow.pickup_date || selectedRow.pickup_hour
                        ? `${formatDate(selectedRow.pickup_date) || ""} ${selectedRow.pickup_hour ? "— " + formatTime12h(selectedRow.pickup_hour) : ""}`.trim()
                        : null,
                  },
                  { label: "موقع الاستقبال", value: selectedRow.pickup_location },
                  { label: "الحي", value: selectedRow.neighborhood === "N/A" ? null : selectedRow.neighborhood },
                  { label: "اسم الفندق", value: selectedRow.hotel_name },
                  { label: "مبلغ البيع", value: selectedRow.price ? `${selectedRow.price} ﷼` : null, highlight: true },
                ]
                  .filter((r) => r.value != null && r.value !== "")
                  .map((r, i, arr) => (
                    <Stack
                      key={r.label}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        px: 1.5,
                        py: 0.9,
                        borderBottom: i < arr.length - 1 ? "1px solid #eee" : "none",
                        bgcolor: i % 2 === 0 ? "#fff" : "#f9f9f9",
                      }}
                    >
                      <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.78rem", color: "#888", minWidth: 90 }}>{r.label}</Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Cairo", sans-serif',
                          fontSize: "0.82rem",
                          fontWeight: r.highlight ? 800 : 600,
                          color: r.highlight ? "#d33030" : r.whatsapp ? "#25D366" : "#222",
                          direction: r.ltr ? "ltr" : "rtl",
                          textAlign: "left",
                        }}
                      >
                        {r.whatsapp ? (
                          <a
                            href={`https://wa.me/${formatPhoneForWhatsApp(r.value)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#25D366", textDecoration: "none", fontWeight: 700 }}
                          >
                            📱 {r.value}
                          </a>
                        ) : (
                          r.value
                        )}
                      </Typography>
                    </Stack>
                  ))}
              </Box>
            </>
          )}

          {saveError && (
            <Alert severity="error" sx={{ mt: 2, fontFamily: '"Cairo", sans-serif', fontSize: "0.82rem", "& .MuiAlert-icon": { marginRight: 0, marginLeft: "12px" } }}>
              {saveError}
            </Alert>
          )}
          {saveSuccess && (
            <Alert severity="success" sx={{ mt: 2, fontFamily: '"Cairo", sans-serif', fontSize: "0.82rem", "& .MuiAlert-icon": { marginRight: 0, marginLeft: "12px" } }}>
              تم الحفظ بنجاح ✓
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
          <Button
            onClick={handleCloseModal}
            variant="outlined"
            color="inherit"
            disabled={saving}
            sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 600, borderRadius: 2, flex: 1 }}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={{
              fontFamily: '"Cairo", sans-serif',
              fontWeight: 700,
              borderRadius: 2,
              flex: 2,
              bgcolor: "#e65100",
              "&:hover": { bgcolor: "#bf360c" },
            }}
          >
            {saving ? "جاري الحفظ..." : "حفظ"}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
